import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {KTIcon} from '../../../../../_metronic/helpers'
import {collection, doc, getDoc, onSnapshot, updateDoc} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useAuth} from '../../../../modules/auth'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'

type Props = {
  id: string
  isOpen: boolean
  onClose: () => void
}

interface Control {
  id: string
  control_category: string
  control_description: string
  control_document: string
  control_evidence_status: string
  control_framework_mappings: string
  control_id: string
  control_owner: string
  control_test: string
  control_title: string
  control_uid: string
  document_id: string[]
  test_id: string[]
}

const getCodeFromFrameworkMapping = (mapping: string) => {
  const parts = mapping.split('‧')
  return parts.length > 1 ? parts[1].trim() : ''
}

const getMainCode = (mapping: string) => {
  const parts = mapping.split('‧')
  if (parts.length > 1) {
    return parts[1].trim().split('.').slice(0, 2).join('.') 
  }
  return ''
}

const getLetterPrefix = (code: string) => {
  return code.match(/[A-Z]/)?.[0] || ''
}

const getNumericPart = (code: string) => {
  return parseInt(code.replace(/[A-Z]\./i, '')) || 0
}

const getHIPAACategory = (frameworkMapping: string): string => {
  const mapping = frameworkMapping.toLowerCase()

  if (mapping.includes('164.308')) {
    return 'Administrative - 164.308'
  } else if (mapping.includes('164.310')) {
    return 'Physical - 164.310'
  } else if (mapping.includes('164.312')) {
    return 'Technical - 164.312'
  } else if (mapping.includes('164.314')) {
    return 'Organizational - 164.314'
  } else if (mapping.includes('164.316')) {
    return 'Documentation - 164.316'
  } else if (
    mapping.includes('164.404') || 
    mapping.includes('164.406') ||
    mapping.includes('164.408') ||
    mapping.includes('164.410')
  ) {
    return 'Breach Notification - 164.404–410'
  } else {
    return 'Other'
  }
}

const HIPAA_CATEGORY_ORDER = [
  'Administrative - 164.308',
  'Organizational - 164.314', 
  'Documentation - 164.316',
  'Breach Notification - 164.404–410',
  'Physical - 164.310',
  'Technical - 164.312',
  'Other'
] as const

const AddFramework: React.FC<Props> = ({id, isOpen, onClose}) => {
  const [risk, setRisk] = useState([] as any[])
  const [control, setControl] = useState<any>([])
  const {currentUser} = useAuth()
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([])
  const [controls, setControls] = useState<Record<string, Record<string, Control[]>>>({})
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    getActiveFramworks()
  }, [])

  useEffect(() => {
    getRisk()
  }, [showRecommendedOnly])

  useEffect(() => {
    getControl()
    getControls()
  }, [activeFrameworks])

  useEffect(() => {
    if (activeFrameworks.length > 0) {
      setSelectedFrameworks([activeFrameworks[0]])
    }
  }, [activeFrameworks])

  const handleFrameworkSelection = (framework: string) => {
    setSelectedFrameworks([framework]) 
  }

  const getRisk = async () => {
    try {
      const tenantId = currentUser?.tenantId || '' 
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'risks')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
          .filter((data) => data.id === id)
        setRisk(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe 
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }
  const getControl = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'controls')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().control_framework_mappings,
          status: doc.data().control_evidence_status,
        }))

        const filteredControls = controlsData.filter((control) => {
          if (!control.framework) return false

          return activeFrameworks.some(
            (framework) =>
              control.framework.startsWith(framework) &&
              !control.framework.startsWith('ISO 27001:2013')
          )
        })

        setControl(filteredControls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const getControls = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'controls')

      const unsubscribe = onSnapshot(controlsCollectionRef, (snapshot) => {
        const controlsData: Control[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          control_category: doc.data().control_category || '',
          control_description: doc.data().control_description || '',
          control_document: doc.data().control_document || '',
          control_evidence_status: doc.data().control_evidence_status || '',
          control_framework_mappings: doc.data().control_framework_mappings || '',
          control_id: doc.data().control_id || '',
          control_owner: doc.data().control_owner || '',
          control_test: doc.data().control_test || '',
          control_title: doc.data().control_title || '',
          control_uid: doc.data().control_uid || '',
          document_id: doc.data().document_id || [],
          test_id: doc.data().test_id || [],
        }))

        const filteredControls = controlsData.filter(
          (control) =>
            activeFrameworks.some((framework) =>
              control.control_framework_mappings.startsWith(framework)
            ) && !control.control_framework_mappings.startsWith('ISO 27001:2013')
        )

        // Group controls by HIPAA category first, then by main code
        const groupedByCategory = filteredControls.reduce((acc, control) => {
          const category = getHIPAACategory(control.control_framework_mappings)
          const mainCode = getMainCode(control.control_framework_mappings)

          if (!acc[category]) {
            acc[category] = {}
          }
          if (!acc[category][mainCode]) {
            acc[category][mainCode] = []
          }
          acc[category][mainCode].push(control)
          return acc
        }, {} as Record<string, Record<string, Control[]>>)

        // Sort controls within each main code group
        Object.keys(groupedByCategory).forEach((category) => {
          Object.keys(groupedByCategory[category]).forEach((mainCode) => {
            groupedByCategory[category][mainCode].sort((a, b) => {
              const codeA = getCodeFromFrameworkMapping(a.control_framework_mappings)
              const codeB = getCodeFromFrameworkMapping(b.control_framework_mappings)
              return codeA.localeCompare(codeB)
            })
          })
        })

        // Sort main codes within each category and sort categories by HIPAA order
        const getNumericParts = (code: string) => {
          return code.match(/\d+/g)?.map(Number) || []
        }

        const sortedGroupedControls = HIPAA_CATEGORY_ORDER.reduce((acc, category) => {
          if (groupedByCategory[category]) {
            const sortedMainCodes = Object.entries(groupedByCategory[category])
              .sort(([keyA], [keyB]) => {
                const prefixA = getLetterPrefix(keyA)
                const prefixB = getLetterPrefix(keyB)
                if (prefixA === 'C' && prefixB !== 'C') {
                  return -1
                } else if (prefixB === 'C' && prefixA !== 'C') {
                  return 1
                }
                if (prefixA !== prefixB) {
                  return prefixA.localeCompare(prefixB)
                }
                const numericPartsA = getNumericParts(keyA)
                const numericPartsB = getNumericParts(keyB)
                for (let i = 0; i < Math.min(numericPartsA.length, numericPartsB.length); i++) {
                  if (numericPartsA[i] !== numericPartsB[i]) {
                    return numericPartsA[i] - numericPartsB[i]
                  }
                }
                return numericPartsA.length - numericPartsB.length
              })
              .reduce((categoryAcc, [mainCode, controls]) => {
                categoryAcc[mainCode] = controls.sort((a, b) => {
                  const codeA = getCodeFromFrameworkMapping(a.control_framework_mappings)
                  const codeB = getCodeFromFrameworkMapping(b.control_framework_mappings)
                  const numericPartsA = getNumericParts(codeA)
                  const numericPartsB = getNumericParts(codeB)
                  for (let i = 0; i < Math.min(numericPartsA.length, numericPartsB.length); i++) {
                    if (numericPartsA[i] !== numericPartsB[i]) {
                      return numericPartsA[i] - numericPartsB[i]
                    }
                  }
                  return numericPartsA.length - numericPartsB.length
                })
                return categoryAcc
              }, {} as Record<string, Control[]>)

            acc[category] = sortedMainCodes
          }
          return acc
        }, {} as Record<string, Record<string, Control[]>>)

        setControls(sortedGroupedControls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    if (control.length > 0 && risk.length > 0) {
      logMatchingRecommendedControls()
    }
  }, [control, risk])

  const logMatchingRecommendedControls = () => {
    const matchingControls = control.filter((item) => {
      return Array.from(recommendedControlsSet as Set<string>).some((rec: string) =>
        item.control_title.toLowerCase().includes(rec.toLowerCase())
      )
    })
  }



  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const recommendedControlsSet = useMemo(() => {
    return new Set(risk[0]?.recommendedControls || [])
  }, [risk])

  const filteredControls = useMemo(() => {
    return Object.entries(controls).filter(([category, categoryControls]) => {
      return Object.entries(categoryControls).some(([mainCode, controlGroup]) => {
        const matchesSearch = controlGroup[0].control_category
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        const matchesSelectedFrameworks =
          selectedFrameworks.length === 0 ||
          selectedFrameworks.some((framework) =>
            controlGroup[0].control_framework_mappings.includes(framework)
          )

        return matchesSearch && matchesSelectedFrameworks
      })
    }).map(([category, categoryControls]) => {
      const filteredCategoryControls = Object.entries(categoryControls).filter(([mainCode, controlGroup]) => {
        const matchesSearch = controlGroup[0].control_category
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        const matchesSelectedFrameworks =
          selectedFrameworks.length === 0 ||
          selectedFrameworks.some((framework) =>
            controlGroup[0].control_framework_mappings.includes(framework)
          )

        return matchesSearch && matchesSelectedFrameworks
      }).reduce((acc, [mainCode, controlGroup]) => {
        acc[mainCode] = controlGroup
        return acc
      }, {} as Record<string, Control[]>)

      return [category, filteredCategoryControls] as [string, Record<string, Control[]>]
    })
  }, [controls, searchTerm, selectedFrameworks])

  const resetSelectedFrameworks = () => {
    setSelectedFrameworks([])
    setSearchTerm('')
  }

  const updateControlFrameworks = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'controls')

      const controlRef = doc(controlsCollectionRef, id)
      const controlDoc = await getDoc(controlRef)

      if (controlDoc.exists()) {

        const framework = selectedFrameworks[0]

        const selectedControl = selectedControls[0]
        let mainCode = ''
        let category = ''
        
        // Find the mainCode and category for the selected control
        for (const [cat, categoryControls] of Object.entries(controls)) {
          for (const [code, controlGroup] of Object.entries(categoryControls)) {
            if (controlGroup.some((control) => control.id === selectedControl)) {
              mainCode = code
              category = cat
              break
            }
          }
          if (mainCode) break
        }
        
        setIsLoading(true)
        if (mainCode) {
          const existingControlsCount = controls[category][mainCode].length
          const specificCategory = controls[category][mainCode][0].control_category

          const newMapping = `${framework} ‧ ${mainCode}.${existingControlsCount + 1}`

          setIsLoading(true)
          await updateDoc(controlRef, {
            control_framework_mappings: newMapping,
            control_uid: `${mainCode}.${existingControlsCount + 1}`,
            control_category: specificCategory,
          })
        }
      }
      showSuccessToast({message: 'Framework mapped successfully'})
      setIsLoading(false)
      getControls()
      setSelectedControls([])
      onClose()
    } catch (error) {
      showErrorToast({message: 'Error mapping framework. Please try again later.'})
      setIsLoading(false)
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        resetSelectedFrameworks()
        onClose()
      }}
      backdropClassName='bg-opacity-1'
      centered
      size='lg'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
      dialogClassName='modal-90w'
    >
      <Modal.Header
        closeButton
        style={{borderTopLeftRadius: '0.475rem', borderTopRightRadius: '0.475rem'}}
      >
        <Modal.Title>Map Framework</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className='p-0 mt-4'
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(80vh - 60px)',
          borderBottomLeftRadius: '0.475rem',
          borderBottomRightRadius: '0.475rem',
          overflow: 'hidden',
        }}
      >
        <div className='px-6'>
          {activeFrameworks.length > 0 ? (
            activeFrameworks.map((framework, index) => (
              <span
                key={index}
                className={`badge px-4 py-2 rounded-pill me-4 fs-7 cursor-pointer ${
                  selectedFrameworks.includes(framework) ? 'bg-light-primary' : ''
                }`}
                style={{
                  border: selectedFrameworks.includes(framework)
                    ? '2px solid #0E9F6E'
                    : '2px solid #E1E1E1',
                  color: selectedFrameworks.includes(framework) ? '#0E9F6E' : '',
                  cursor: 'pointer',
                }}
                onClick={() => handleFrameworkSelection(framework)}
              >
                {framework}
              </span>
            ))
          ) : (
            <>
              <div className='card p-4 fs-6 fw-semibold cursor-pointer bg-light-danger'>
                <p className='p-0 m-0 px-3'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='30'
                    height='30'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-shield-question me-2 mb-1'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M15.065 19.732c-.95 .557 -1.98 .986 -3.065 1.268a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3c.51 1.738 .617 3.55 .333 5.303' />
                    <path d='M19 22v.01' />
                    <path d='M19 19a2.003 2.003 0 0 0 .914 -3.782a1.98 1.98 0 0 0 -2.414 .483' />
                  </svg>
                  <span className='mt-2'>Framework not found!!</span>
                </p>
              </div>
            </>
          )}
        </div>
        <div className='static-content'>
          <div className='d-flex align-items-center position-relative my-8 px-6 '>
            <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
            <input
              type='text'
              data-kt-user-table-filter='search'
              className='form-control ps-14'
              placeholder='Search..'
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className='table-container' style={{overflowY: 'auto', flexGrow: 1}}>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-hover'>
            <thead>
              <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-top-2 border-gray-200'>
                <th className='min-w-50px'></th>
                <th className='min-w-500px'>REQUIREMENTS</th>
                {/* <th className='min-w-200px'>MAPPED FRAMEWORKS</th> */}
              </tr>
            </thead>
            <tbody>
              {filteredControls.length > 0 ? (
                filteredControls.map(([category, categoryControls]) => 
                  Object.entries(categoryControls).map(([mainCode, controlGroup]) => (
                    <tr key={`${category}-${mainCode}`} className='px-6'>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid ps-6'>
                          <input
                            className='form-check-input widget-9-check'
                            type='checkbox'
                            value={controlGroup[0].id}
                            checked={selectedControls.includes(controlGroup[0].id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedControls([...selectedControls, controlGroup[0].id])
                              } else {
                                setSelectedControls(
                                  selectedControls.filter((id) => id !== controlGroup[0].id)
                                )
                              }
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='fw-semibold p-0'>
                          <a href='#' className='fs-6 fw-bold text-gray-900 text-hover-primary'>
                            {mainCode.split(',')[0].trim()}
                          </a>
                          <div className='fs-7'>{controlGroup[0].control_category}</div>
                        </div>
                      </td>
                    </tr>
                  ))
                ).flat()
              ) : (
                <tr>
                  <td colSpan={10} className='text-center'>
                    <div className=''>
                      <img
                        src='/media/buttons/emptystate.svg'
                        width={100}
                        height={100}
                        alt='Empty State'
                      />
                      <div className='m-5 fs-6'>
                        <p style={{fontWeight: 'bold'}}>
                          {searchTerm ? 'No results found for your search' : 'No Framework Data Available'}
                        </p>
                      </div>
                      <p>
                        {searchTerm ? (
                          'Try adjusting your search term or clear the search to see all frameworks'
                        ) : (
                          <>This framework is not assigned to this account. Check our{' '}
                            <a
                              href='https://getsecureslate.com/pricing'
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-primary ms-1 fw-semibold'
                            >
                              pricing
                            </a>{' '}
                            page for more information.</>
                        )}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className='modal-footer p-0 my-4 pt-4 px-6'>
          <button
            className='btn btn-sm btn-light fs-6'
            onClick={() => {
              resetSelectedFrameworks()
              onClose()
            }}
            style={{
              width: '80px',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
          >
            Cancel
          </button>

          {isLoading ? (
            <button
              className='btn btn-sm fs-6'
              type='submit'
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
              style={{
                backgroundColor: '#0E9F6E',
                color: '#fff',
                width: '200px',
                height: '40px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
              }}
            >
              Map Frameworks...{' '}
              <span className='spinner-border spinner-border-sm align-middle'></span>
            </button>
          ) : (
            <button
              className='btn btn-sm fs-6'
              type='submit'
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
              style={{
                backgroundColor: '#0E9F6E',
                color: '#fff',
                width: '150px',
                height: '40px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
              }}
              onClick={updateControlFrameworks}
            >
              Map Frameworks
            </button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AddFramework
